import cookies from 'js-cookie';
import { find, snakeCase } from 'lodash';
import { appQueryParameters } from '@clubmed/utils';
import URI from 'urijs';
import { setInSessionStorage } from 'Client/session-storage-api/sessionStorageApi';
import {
  PAYMENT_METHOD_LABEL,
  CREDIT_CARD_LABEL,
  VOXPAY_PSP_ID_PREFIX,
  PAYPAL_PSP_ID,
  PAYMENT_METHOD_CATEGORY,
} from 'Components/widgets/payment-info/PaymentInfo.const';
import { BE_TRACKING, GA_DEFAULT_PAYLOAD } from 'Tracking/constants';
import * as trackingStorageApi from 'Tracking/tracking-storage-api';
import { DONATION_STORAGE_KEY, JOYVIEW_UPSELL_STORAGE_KEY } from 'Utils/payment/constants';
import { PACKAGE_FOOD_AND_BEVERAGE, PACKAGE_ROOM_ONLY } from 'Utils/proposal/constants';
import { isStrictlyDevelopmentEnv } from 'Utils/context/contextUtils';
import type { TrackPayementFunction } from 'Src/types/payment';
const APPROVE_ROUTE = '/approve-payment';
const ERROR_CODE_LABEL = {
  BOOKING_ALREADY_EXISTS: 'booking.banking.aBookingAlreadyExists2',
  BOOKING_UNAVAILABLE: 'booking.banking.confirmation.itemOfTheBookingNotAvailable',
  INVALID_EMAIL: 'booking.partners.linkPayment.email.invalidEmail',
  INVALID_PHONE_NUMBER: 'booking.partners.linkPayment.sms.invalidNumber',
  PAYMENT_ALREADY_SCHEDULED: 'booking.partners.errorMessages.automaticPaymentScheduled',
  PAYMENT_ALREADY_DONE: 'booking.banking.paymentAlreadyDone',
  PAYMENT_ALREADY_IN_PROGRESS: 'booking.banking.paymentAlreadyInProgress',
  FLIGHT_AVAILABILITY_EXPIRED: 'booking.banking.timeLimitExceeded',
};

function getPaymentData(items: Array<any> = [], id?: string | null): any {
  return (
    find(items, {
      id: id,
    }) || items[0]
  );
}

function getPaymentErrorMessagePath({
  errorCode,
  track,
  isCmta = false,
}: {
  errorCode?: string;
  track: (arg0: any) => void;
  isCmta?: boolean;
}): string {
  const trackingData = { ...GA_DEFAULT_PAYLOAD, actionGA: 'banking' };
  const errorCodeLabel =
    ERROR_CODE_LABEL[errorCode as keyof typeof ERROR_CODE_LABEL] ||
    (isCmta
      ? 'booking.partners.banking.paymentErrorMessage'
      : 'booking.banking.paymentErrorMessage');
  track?.({
    ...trackingData,
    ...(errorCode && {
      labelGA: `error_${errorCode}`,
    }),
  });
  return errorCodeLabel;
}

type ApproveRouteParams = {
  appContext: AppContext;
  router: { asPath: string };
  eventParams: Record<string, string>;
};

function getApproveRoute({ appContext, router, eventParams }: ApproveRouteParams): string {
  return new URI(router.asPath)
    .path(APPROVE_ROUTE)
    .query(eventParams)
    .setQuery(appQueryParameters.locale, appContext?.locale)
    .toString();
}

const TRACKING_FULL_PAYMENT = 'full_payment';
const TRACKING_DOWN_PAYMENT = 'down_payment';
const TRACKING_FRACTION_PAYMENT = 'fraction_payment';
type TrackPaymentOptions = {
  proposal: Proposal;
  track: (arg0: any) => void;
  donationAmount: number;
};
type TrackPaymentPayload = {
  orderId: string | null;
  isErrored?: boolean;
  bookingId?: string;
  amountToPay: number;
  errorMsg?: string;
  paymentMethodLabel: string;
  paymentProviderId: string;
};

export type { TrackPayementFunction };

function getPaymentMethodLabel(
  paymentProviderId: string | undefined,
  paymentMethodLabel: string | undefined,
): string {
  if (paymentProviderId?.startsWith(VOXPAY_PSP_ID_PREFIX)) {
    // on the api side, voxpay is one psp with 2 parameters (email, phone number) with 0 payment method
    // on the front side, voxpay is displayed as two different psp (one for email, one for phone number) with 0 payment method
    // on the tracking side, voxpay is tracked like one psp with 2 payment method (email and sms)
    return cookies.get(PAYMENT_METHOD_LABEL);
  }

  return paymentMethodLabel || cookies.get(PAYMENT_METHOD_LABEL) || CREDIT_CARD_LABEL;
}

export const trackPaymentPlan = ({
  paymentMethodCategory,
  isFullPayment,
}: {
  paymentMethodCategory?: string;
  isFullPayment: boolean;
}) => {
  const isBNPLPayment = paymentMethodCategory === PAYMENT_METHOD_CATEGORY.BUY_NOW_PAY_LATER;
  const fullPaymentPlan = isBNPLPayment ? TRACKING_FRACTION_PAYMENT : TRACKING_FULL_PAYMENT;
  return isFullPayment ? fullPaymentPlan : TRACKING_DOWN_PAYMENT;
};
export const getTrackPaymentFunc = ({
  proposal,
  track,
  donationAmount,
}: TrackPaymentOptions): TrackPayementFunction => {
  return ({
    orderId,
    isErrored,
    bookingId,
    amountToPay,
    paymentMethodLabel,
    paymentProviderId,
  }) => {
    const fullPayment = amountToPay === proposal.price.total.value;
    const paymentPlan = trackPaymentPlan({
      isFullPayment: fullPayment,
      paymentMethodCategory: paymentMethodLabel,
    });
    const paymentMethod = getPaymentMethodLabel(paymentProviderId, paymentMethodLabel);
    track({
      orderID_PSP: orderId,
      labelGA: 'clickValidateBanking',
      paymentMethod,
    });
    track({
      labelGA: isErrored ? 'errorValidateBanking' : 'validateBanking',
      providerId: paymentProviderId,
      paymentPlan,
    });

    if (!isErrored) {
      const beTracking =
        (trackingStorageApi.get(BE_TRACKING) as Record<string, unknown> | null | undefined) ?? {};
      const paymentType = paymentProviderId === PAYPAL_PSP_ID ? 'PAYPAL' : paymentProviderId;
      const paymentTracking = {
        event: 'payment',
        basePriceConfirmation: proposal.price.total.value,
        paymentPlan,
        paymentDeposit: fullPayment ? null : amountToPay,
        paymentType,
        transactionID: bookingId,
        orderID_PSP: orderId,
        paymentMethod,
      };
      const paymentTrackingProps = { ...beTracking, ...paymentTracking };
      trackingStorageApi.set(paymentTrackingProps);

      /**
       * Store donation amount in session storage to show thanks message on booking confirmation page
       * It's not possible to use price_details api route : get /v1/customers/{customer_id}/bookings/{booking_id}/price_details
       * because it needs an authentication token, that we do not have for unauthenticated users
       */
      if (donationAmount) {
        setInSessionStorage(
          DONATION_STORAGE_KEY,
          donationAmount as unknown as Record<string, unknown>,
        );
      }

      // store, when the proposal is joyview, the upSell price of package, for confirmation tracking purpose
      if (proposal.alternativePackages && proposal.alternativePackages.length > 0) {
        const basePackage =
          proposal.alternativePackages.find(({ packageId }) => packageId === PACKAGE_ROOM_ONLY) ||
          proposal.alternativePackages.find(
            ({ packageId }) => packageId === PACKAGE_FOOD_AND_BEVERAGE,
          );

        if (basePackage) {
          setInSessionStorage(
            JOYVIEW_UPSELL_STORAGE_KEY,
            basePackage.upsellPrice.replace(/\D*/, '') as unknown as Record<string, unknown>,
          );
        }
      }
    }
  };
};

const mapTransportResume = (transports?: any) => {
  return transports
    ? [transports.inbound, transports.outbound].map(({ travelSections }) =>
        travelSections.map(({ transport, departure, arrival }) => ({
          company: {
            name: transport.companyOperator.label,
            id: transport.companyOperator.id,
          },
          origin: departure.cityId,
          destination: arrival.cityId,
          departure: {
            apc: departure.id,
            date: departure.date,
          },
          arrival: {
            apc: arrival.id,
            date: arrival.date,
          },
        })),
      )
    : [];
};

const persistPaymentMethodInCookies = ({
  paymentProduct,
  paymentProvider,
  paymentMethodId,
}: {
  paymentProduct?: string;
  paymentProvider?: PaymentProvider;
  paymentMethodId: string;
}) => {
  let paymentMethodLabel = paymentProvider?.categoryPaymentMethod || 'unknown';

  if (paymentMethodLabel === PAYMENT_METHOD_CATEGORY.CREDIT_CARD) {
    paymentMethodLabel =
      paymentProduct ||
      paymentProvider?.paymentMethods.find(({ id }) => id === paymentMethodId)?.label ||
      paymentMethodLabel;
  }

  cookies.set(PAYMENT_METHOD_LABEL, snakeCase(paymentMethodLabel), {
    secure: !isStrictlyDevelopmentEnv(),
  });
};

export {
  mapTransportResume,
  getPaymentData,
  getApproveRoute,
  getPaymentErrorMessagePath,
  persistPaymentMethodInCookies,
};
